import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Gift, Trophy, X, CheckCircle2, Clock, Plus, Minus } from "lucide-react";

export default function AdminReferrals() {
  const [data, setData] = useState({ referrals: [], leaderboard: [] });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | pending | converted
  const [creditFor, setCreditFor] = useState(null); // user obj
  const [creditWeeks, setCreditWeeks] = useState(1);
  const [creditReason, setCreditReason] = useState("");
  const [creditBusy, setCreditBusy] = useState(false);

  const reload = () => {
    setLoading(true);
    api.get("/admin/referrals").then(r => setData(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { reload(); }, []);

  const submitCredit = async () => {
    if (!creditFor || creditWeeks === 0) return;
    setCreditBusy(true);
    try {
      const { data: res } = await api.post(`/admin/users/${creditFor.user_id}/credit-weeks`, {
        weeks: Number(creditWeeks),
        reason: creditReason.trim() || null,
      });
      toast.success(`${creditWeeks > 0 ? "+" : ""}${creditWeeks} week${Math.abs(creditWeeks) !== 1 ? "s" : ""} → ${res.user} (new total: ${res.new_total})`);
      setCreditFor(null);
      setCreditWeeks(1);
      setCreditReason("");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Credit failed");
    }
    setCreditBusy(false);
  };

  const filtered = data.referrals.filter(r => filter === "all" ? true : r.status === filter);
  const totalConverted = data.referrals.filter(r => r.status === "converted").length;
  const totalPending = data.referrals.filter(r => r.status === "pending").length;
  const totalWeeksAwarded = data.leaderboard.reduce((s, u) => s + (u.bonus_weeks_earned || 0), 0);

  return (
    <div data-testid="admin-referrals">
      <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#d7ff00]">REFERRALS</div>
      <h1 className="font-display text-4xl uppercase">Runner <span className="text-[#d7ff00]">network</span></h1>

      {/* KPIs */}
      <div className="mt-6 grid sm:grid-cols-4 gap-3">
        <Kpi label="Total invites" value={data.referrals.length} icon={Gift} />
        <Kpi label="Converted" value={totalConverted} icon={CheckCircle2} accent="#d7ff00" />
        <Kpi label="Pending" value={totalPending} icon={Clock} accent="#ff9500" />
        <Kpi label="Weeks awarded" value={totalWeeksAwarded} icon={Trophy} accent="#d7ff00" />
      </div>

      {/* Leaderboard */}
      <div className="mt-8">
        <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#bbb] mb-3 flex items-center gap-1.5"><Trophy className="w-3 h-3 text-[#d7ff00]" /> TOP REFERRERS</div>
        <div className="bg-[#0a0a0a] border border-[#1f1f1f] divide-y divide-[#1f1f1f]">
          {data.leaderboard.length === 0 && <div className="p-5 text-[#888] text-sm">No referrals yet.</div>}
          {data.leaderboard.slice(0, 10).map((u, i) => (
            <div key={u.user_id} className="p-4 flex items-center justify-between flex-wrap gap-3" data-testid={`leader-${u.user_id}`}>
              <div className="flex items-center gap-3 min-w-0">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-display ${i < 3 ? "bg-[#d7ff00]/15 text-[#d7ff00]" : "bg-[#1f1f1f] text-[#bbb]"}`}>{i + 1}</div>
                <div className="min-w-0">
                  <div className="font-display text-base truncate">{u.name || "—"}</div>
                  <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#888] truncate">{u.email} · {u.code}</div>
                </div>
              </div>
              <div className="flex items-center gap-4 flex-wrap">
                <Stat label="Invited" value={u.invited} />
                <Stat label="Converted" value={u.converted} accent="#d7ff00" />
                <Stat label="Bonus weeks" value={u.bonus_weeks_earned || 0} accent="#d7ff00" />
                <button onClick={() => { setCreditFor(u); setCreditWeeks(1); setCreditReason(""); }} className="bg-[#d7ff00] text-black font-mono-jp text-[10px] uppercase tracking-widest px-3 py-1.5 hover:bg-white flex items-center gap-1" data-testid={`credit-${u.user_id}`}>
                  <Plus className="w-3 h-3" /> Credit weeks
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* All referrals table */}
      <div className="mt-8">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#bbb] flex items-center gap-1.5"><Gift className="w-3 h-3 text-[#d7ff00]" /> ALL REFERRALS</div>
          <div className="flex gap-1">
            <Chip active={filter === "all"} onClick={() => setFilter("all")} testid="ref-filter-all">All · {data.referrals.length}</Chip>
            <Chip active={filter === "converted"} onClick={() => setFilter("converted")} testid="ref-filter-converted">Converted · {totalConverted}</Chip>
            <Chip active={filter === "pending"} onClick={() => setFilter("pending")} testid="ref-filter-pending">Pending · {totalPending}</Chip>
          </div>
        </div>
        <div className="bg-[#0a0a0a] border border-[#1f1f1f] overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left font-mono-jp text-[10px] uppercase tracking-widest text-[#888] border-b border-[#1f1f1f]">
                <th className="px-4 py-3">Referrer</th>
                <th className="px-4 py-3">Referee</th>
                <th className="px-4 py-3">Code</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Created</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={5} className="px-4 py-5 text-[#888]">Loading…</td></tr>}
              {!loading && filtered.length === 0 && <tr><td colSpan={5} className="px-4 py-5 text-[#888]">No referrals match this filter.</td></tr>}
              {filtered.map(r => (
                <tr key={r.id} className="border-b border-[#141414] last:border-b-0" data-testid={`ref-row-${r.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-white">{r.referrer_name || "—"}</div>
                    <div className="font-mono-jp text-[10px] text-[#888]">{r.referrer_email}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-white">{r.referee_name}</div>
                    <div className="font-mono-jp text-[10px] text-[#888]">{r.referee_email}</div>
                  </td>
                  <td className="px-4 py-3 font-mono-jp text-xs text-[#d7ff00]">{r.referrer_code}</td>
                  <td className="px-4 py-3">
                    {r.status === "converted" ? (
                      <span className="inline-flex items-center gap-1 font-mono-jp text-[10px] uppercase tracking-widest text-[#d7ff00]"><CheckCircle2 className="w-3 h-3" /> Converted</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 font-mono-jp text-[10px] uppercase tracking-widest text-[#ff9500]"><Clock className="w-3 h-3" /> Pending</span>
                    )}
                  </td>
                  <td className="px-4 py-3 font-mono-jp text-[10px] text-[#bbb]">{(r.created_at || "").slice(0, 10)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Credit weeks modal */}
      {creditFor && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => !creditBusy && setCreditFor(null)} data-testid="credit-modal">
          <div className="bg-[#0a0a0a] border border-[#d7ff00] w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-4">
              <div>
                <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#d7ff00] flex items-center gap-1.5"><Gift className="w-3 h-3" /> MANUAL CREDIT</div>
                <h3 className="font-display text-2xl uppercase mt-1">{creditFor.name}</h3>
                <p className="text-[10px] font-mono-jp text-[#888] uppercase tracking-widest mt-0.5">Current total: {creditFor.bonus_weeks_earned || 0} bonus week{(creditFor.bonus_weeks_earned || 0) !== 1 ? "s" : ""}</p>
              </div>
              {!creditBusy && <button onClick={() => setCreditFor(null)} className="text-[#888]" data-testid="credit-close"><X /></button>}
            </div>

            <label className="font-mono-jp text-[10px] uppercase tracking-widest text-[#bbb]">Weeks to credit</label>
            <div className="mt-2 flex items-center gap-2">
              <button onClick={() => setCreditWeeks(Math.max(-10, Number(creditWeeks) - 1))} className="bg-[#0e0e0e] border border-[#222] hover:border-[#d7ff00] w-10 h-10 flex items-center justify-center" data-testid="credit-minus"><Minus className="w-4 h-4" /></button>
              <input type="number" value={creditWeeks} onChange={e => setCreditWeeks(Number(e.target.value))} className="flex-1 bg-[#0e0e0e] border border-[#222] focus:border-[#d7ff00] outline-none px-3 py-2.5 text-center font-display text-2xl text-[#d7ff00]" data-testid="credit-weeks-input" />
              <button onClick={() => setCreditWeeks(Math.min(10, Number(creditWeeks) + 1))} className="bg-[#0e0e0e] border border-[#222] hover:border-[#d7ff00] w-10 h-10 flex items-center justify-center" data-testid="credit-plus"><Plus className="w-4 h-4" /></button>
            </div>
            <p className="mt-2 text-[10px] font-mono-jp text-[#666] uppercase tracking-widest">Use negative values to deduct.</p>

            <label className="mt-4 block font-mono-jp text-[10px] uppercase tracking-widest text-[#bbb]">Reason (optional)</label>
            <input value={creditReason} onChange={e => setCreditReason(e.target.value)} placeholder="e.g. Goodwill gesture, missed promo, etc." className="mt-2 w-full bg-[#0e0e0e] border border-[#222] focus:border-[#d7ff00] outline-none px-3 py-2.5 text-sm" data-testid="credit-reason" />

            <button onClick={submitCredit} disabled={creditBusy || creditWeeks === 0} className="mt-5 w-full bg-[#d7ff00] text-black font-display uppercase tracking-wider py-3 hover:bg-white disabled:opacity-50 flex items-center justify-center gap-2" data-testid="credit-confirm">
              <Gift className="w-4 h-4" /> {creditBusy ? "Crediting…" : `${creditWeeks > 0 ? "Credit" : "Deduct"} ${Math.abs(creditWeeks)} week${Math.abs(creditWeeks) !== 1 ? "s" : ""}`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value, icon: Icon, accent = "#bbb" }) {
  return (
    <div className="bg-[#0a0a0a] border border-[#1f1f1f] p-4">
      <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#bbb] flex items-center gap-1.5">
        <Icon className="w-3 h-3" style={{ color: accent }} /> {label}
      </div>
      <div className="font-display text-3xl mt-1" style={{ color: accent }}>{value}</div>
    </div>
  );
}

function Stat({ label, value, accent = "#fff" }) {
  return (
    <div className="text-center min-w-[58px]">
      <div className="font-display text-xl" style={{ color: accent }}>{value}</div>
      <div className="font-mono-jp text-[9px] uppercase tracking-widest text-[#888]">{label}</div>
    </div>
  );
}

function Chip({ active, onClick, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`px-3 py-1.5 font-mono-jp text-[10px] uppercase tracking-widest border ${active ? "border-[#d7ff00] text-[#d7ff00] bg-[#d7ff00]/10" : "border-[#222] text-[#bbb] hover:border-[#d7ff00] hover:text-white"}`}>
      {children}
    </button>
  );
}
